import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Droplets, Flame, Snowflake, Gauge, Clock, Star, Instagram } from 'lucide-react';
import { BUSINESS_HOURS, GBP_REVIEW_URL, INSTAGRAM_URL, SHOW_WARRANTY_LINE, WARRANTY_LINE } from '../data/constants';

const LOGO_ICON_URL = "/logo-icon.png";
const LOGO_TEXT_URL = "/logo-text.png";
const LOGO_ICON_WEBP = "/logo-icon.webp";
const LOGO_TEXT_WEBP = "/logo-text.webp";
const PHONE = "587-355-FLOW (3569)";
const EMAIL = "service@flowforgemechanical.ca";

const services = [
  { name: 'Plumbing', path: '/plumbing', icon: Droplets },
  { name: 'Heating', path: '/heating', icon: Flame },
  { name: 'Air Conditioning', path: '/air-conditioning', icon: Snowflake },
  { name: 'Gas Fitting', path: '/gas-fitting', icon: Gauge },
];

const popularServices = [
  { name: 'Water Heater Replacement', path: '/plumbing/water-heater-replacement-calgary' },
  { name: 'Furnace Repair', path: '/heating/furnace-installation-repair-calgary' },
  { name: 'Drain Cleaning', path: '/plumbing/drain-cleaning-calgary' },
  { name: 'BBQ Gas Lines', path: '/gas-fitting/bbq-gas-lines-calgary' },
  { name: 'Poly-B Replacement', path: '/plumbing/poly-b-replacement-calgary' },
  { name: 'Backflow Testing', path: '/plumbing/backflow-testing-calgary' },
];

const quickLinks = [
  { name: 'About Us', path: '/about' },
  { name: 'Service Areas', path: '/service-areas' },
  { name: 'Expert Tips', path: '/blog' },
  { name: 'Customer Login', path: '/login' },
  { name: 'Contact', path: '/contact' },
  { name: 'Privacy Policy', path: '/privacy-policy' },
  { name: 'Terms of Service', path: '/terms' },
];

const serviceAreas = [
  { name: 'Calgary', path: '/service-areas' },
  { name: 'Airdrie', path: '/service-areas/airdrie' },
  { name: 'Cochrane', path: '/service-areas/cochrane' },
  { name: 'Okotoks', path: '/service-areas/okotoks' },
  { name: 'Chestermere', path: '/service-areas/chestermere' },
  { name: 'Strathmore', path: '/service-areas/strathmore' },
  { name: 'High River', path: '/service-areas/high-river' },
  { name: 'Rocky View County', path: '/service-areas/rocky-view-county' },
];

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer data-testid="main-footer" className="bg-gray-900 text-white">
      {/* Main Footer */}
      <div className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {/* Company Info */}
            <div>
              {/* Horizontal Logo - Icon + Text */}
              <Link to="/" className="flex items-center gap-3 mb-6">
                <picture>
                  <source srcSet={LOGO_ICON_WEBP} type="image/webp" />
                  <img 
                    src={LOGO_ICON_URL} 
                    alt="" 
                    width="139"
                    height="160"
                    className="h-14 sm:h-16 w-auto object-contain"
                  />
                </picture>
                <picture>
                  <source srcSet={LOGO_TEXT_WEBP} type="image/webp" />
                  <img 
                    src={LOGO_TEXT_URL} 
                    alt="Flow Forge Mechanical" 
                    width="308"
                    height="96"
                    className="h-12 sm:h-14 w-auto object-contain"
                  />
                </picture>
              </Link>
              <p className="text-gray-400 text-sm mb-4">
                Calgary's trusted experts in plumbing, heating, air conditioning, and gas fitting.
              </p>
              <p className="text-[#1E3A5F] text-sm font-semibold mb-6">
                Residential • Building Maintenance • Light Commercial
              </p>
              <div className="space-y-3">
                <a 
                  href={`tel:+15873553569`}
                  data-testid="footer-phone"
                  className="flex items-center gap-3 text-gray-300 hover:text-[#1E3A5F] transition-colors"
                >
                  <Phone className="w-5 h-5 text-[#1E3A5F]" />
                  <span>{PHONE}</span>
                </a>
                <a 
                  href={`mailto:${EMAIL}`}
                  data-testid="footer-email"
                  className="flex items-center gap-3 text-gray-300 hover:text-[#1E3A5F] transition-colors"
                >
                  <Mail className="w-5 h-5 text-[#1E3A5F]" />
                  <span>{EMAIL}</span>
                </a>
                <div className="flex items-center gap-3 text-gray-400">
                  <MapPin className="w-5 h-5 text-[#1E3A5F]" />
                  <span>Calgary, Alberta</span>
                </div>
              </div>
            </div>

            {/* Services */}
            <div>
              <h4 className="font-heading font-bold text-white text-lg mb-6">Services</h4>
              <ul className="space-y-3">
                {services.map((service) => (
                  <li key={service.name}>
                    <Link
                      to={service.path}
                      data-testid={`footer-service-${service.name.toLowerCase().replace(' ', '-')}`}
                      className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
                    >
                      <service.icon className="w-4 h-4 text-[#3B82F6]" />
                      <span>{service.name}</span>
                    </Link>
                  </li>
                ))}
              </ul>
              
              <h4 className="font-heading font-bold text-white text-lg mt-8 mb-4">Popular Services</h4>
              <ul className="space-y-2">
                {popularServices.map((service) => (
                  <li key={service.name}>
                    <Link
                      to={service.path}
                      className="text-gray-400 hover:text-white transition-colors text-sm"
                    >
                      {service.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-heading font-bold text-white text-lg mb-6">Quick Links</h4>
              <ul className="space-y-3">
                {quickLinks.map((link) => (
                  <li key={link.name}>
                    <Link
                      to={link.path}
                      data-testid={`footer-link-${link.name.toLowerCase().replace(' ', '-')}`}
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Service Areas & Hours */}
            <div>
              <h4 className="font-heading font-bold text-white text-lg mb-6">Service Areas</h4>
              <ul className="space-y-2 mb-8">
                {serviceAreas.map((area) => (
                  <li key={area.name}>
                    <Link to={area.path} className="text-gray-400 hover:text-white text-sm transition-colors">{area.name}</Link>
                  </li>
                ))}
              </ul>
              
              <h4 className="font-heading font-bold text-white text-lg mb-4">Operating Hours</h4>
              <div className="space-y-2 text-gray-400 text-sm">
                <div className="flex items-start gap-2">
                  <Clock className="w-4 h-4 text-[#3B82F6] mt-0.5" />
                  <div>
                    <p>{BUSINESS_HOURS.weekdays}</p>
                    <p className="mt-1">{BUSINESS_HOURS.weekend}</p>
                    <p className="mt-2 text-gray-500" data-testid="footer-holiday-closures">{BUSINESS_HOURS.holidayClosuresNote}</p>
                  </div>
                </div>
                {SHOW_WARRANTY_LINE && (
                  <p className="mt-3 text-gray-500 text-xs" data-testid="footer-warranty-line">{WARRANTY_LINE}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-sm">
              &copy; {currentYear} Flow Forge Mechanical. All rights reserved.
            </p>
            <div className="flex items-center gap-6 text-sm text-gray-500">
              <span>Licensed &amp; Insured</span>
              <a
                href={GBP_REVIEW_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-google-review"
                className="flex items-center gap-1.5 hover:text-white transition-colors"
              >
                <Star className="w-4 h-4" />
                Leave a Google Review
              </a>
              {INSTAGRAM_URL && (
                <a
                  href={INSTAGRAM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-instagram"
                  aria-label="Flow Forge Mechanical on Instagram"
                  className="hover:text-white transition-colors"
                >
                  <Instagram className="w-5 h-5" />
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
